import { FC, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { InfoCard, Progress } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { DevHubTypography, DevHubAlert } from '@devxdiscover/devhub-ui';
import { useAppId } from '../../../contexts/SystemContext';
import { GET_CLOUD_FOUNDRY_APPLICATIONS } from './CloudFoundryQueries';
import { CloudFoundryTable } from './CloudFoundryTable';
import type {
  CloudFoundryApplicationsResponse,
  CloudFoundryApplicationsVariables,
} from './types';

/**
 * Component displaying Cloud Foundry resources associated with an application
 * @returns Cloud Foundry resources view with table or appropriate message
 */
export const ApplicationCloudFoundry: FC = () => {
  const { entity } = useEntity();
  const appId = useAppId();

  const { loading, data, error } = useQuery<
    CloudFoundryApplicationsResponse,
    CloudFoundryApplicationsVariables
  >(GET_CLOUD_FOUNDRY_APPLICATIONS, {
    variables: { applicationId: appId },
    skip: !appId,
    errorPolicy: 'all',
  });

  const applications = useMemo(
    () =>
      data?.resources?.application?.resources?.cloudFoundryApplicationsPaged
        ?.cloudFoundryApplications || [],
    [data],
  );

  const totalCount = useMemo(
    () =>
      data?.resources?.application?.resources?.cloudFoundryApplicationsPaged
        ?.totalCount || 0,
    [data],
  );

  const subtitleText = useMemo(() => {
    const resourceText =
      totalCount === 1 ? 'cloud foundry resource' : 'cloud foundry resources';
    return `${totalCount} ${resourceText} associated with ${entity.metadata.title}`;
  }, [totalCount, entity.metadata.title]);

  if (loading) {
    return (
      <InfoCard title="Cloud Foundry">
        <Progress />
      </InfoCard>
    );
  }

  if (error && !data) {
    return (
      <InfoCard title="Cloud Foundry">
        <DevHubAlert severity="error">
          Error loading Cloud Foundry data: {error.message}
        </DevHubAlert>
      </InfoCard>
    );
  }

  if (!loading && applications.length === 0) {
    return (
      <InfoCard title="Cloud Foundry">
        <DevHubTypography variant="subtitle1" style={{ paddingBottom: '10px' }}>
          {subtitleText}
        </DevHubTypography>
        <DevHubTypography variant="body1">
          No Cloud Foundry applications found.
        </DevHubTypography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Cloud Foundry">
      <DevHubTypography variant="subtitle1" style={{ paddingBottom: '10px' }}>
        {subtitleText}
      </DevHubTypography>
      <CloudFoundryTable
        applications={applications}
        loading={loading}
        totalCount={totalCount}
      />
    </InfoCard>
  );
};
